import { useEffect, useRef } from 'react';
import { useSharedSocket } from './useSharedSocket.js';

// Signalen die betekenen "er is iets veranderd" → één keer opnieuw ophalen. Het signaal draagt
// geen data; de API blijft de bron. Ook de meldingsbel (useHealthAlerts, usePushMeldingen) loopt hierdoor.
export const VERVERS_SIGNALEN = [
    'health-alert',
    'push-melding',
    'security:lockout',
    'project:status',
    'task:updated',
];

/**
 * Haalt één keer op, en daarna alleen nog op een push-signaal, na een herstelde verbinding of
 * bij terugkeer naar de voorgrond.
 *
 * `decisions/geen-polling.md` verbiedt "elke X seconden ophalen", maar op 22-08-2026 deden drie
 * schermen het alsnog: StatusView elke 30s, TasksView elke 30s, ProjectsView elke 60s. Ze stonden
 * er los in, elk met een eigen setInterval — daarom staat het patroon nu op één plek.
 *
 * De refetch ná reconnect is geen polling maar de fallback die het besluit expliciet toestaat:
 * zolang de socket weg was kan er van alles veranderd zijn, en dat missen we anders stilzwijgend.
 * Terugkeer naar de voorgrond om dezelfde reden: een telefoon verbreekt de socket in de
 * achtergrond, en de 'connect' komt pas na de herverbinding (24-10-2026: gesloten alert bleef open).
 *
 * @param {string} token   Auth-token; zonder token geen socket.
 * @param {Function} fetch Stabiele functie (useCallback) die de data ophaalt.
 */
export function useLiveRefresh(token, fetch) {
    const socket = useSharedSocket(token);
    const eersteVerbinding = useRef(true);

    useEffect(() => {
        fetch();
    }, [fetch]);

    useEffect(() => {
        const opZichtbaar = () => {
            if (document.visibilityState === 'visible') fetch();
        };

        document.addEventListener('visibilitychange', opZichtbaar);

        return () => document.removeEventListener('visibilitychange', opZichtbaar);
    }, [fetch]);

    useEffect(() => {
        if (!socket) return undefined;

        const opSignaal = () => fetch();
        const opVerbinding = () => {
            if (eersteVerbinding.current) {
                // De verbinding die bij het opstarten hoort; er is net opgehaald.
                eersteVerbinding.current = false;

                return;
            }
            fetch();
        };

        VERVERS_SIGNALEN.forEach((signaal) => socket.on(signaal, opSignaal));
        socket.on('connect', opVerbinding);

        return () => {
            VERVERS_SIGNALEN.forEach((signaal) => socket.off(signaal, opSignaal));
            socket.off('connect', opVerbinding);
        };
    }, [socket, fetch]);

    return socket;
}

export default useLiveRefresh;
